import React, { useState } from "react";
import { X } from "lucide-react";
import { db } from "../../firebase/config";
import { doc, updateDoc } from "firebase/firestore";

export default function ManageChannelMembersModal({ isOpen, onClose, channel, users, currentUserEmail }) {
  if (!isOpen || !channel) return null;

  // Start with the current invitedMembers or empty list
  const currentInvited = channel.invitedMembers || [];
  const [invitedEmails, setInvitedEmails] = useState(currentInvited);
  const [saving, setSaving] = useState(false);

  const capitalizeName = (name) => {
    if (!name) return "";
    return name.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  const getFormattedName = (u) => {
    if (!u) return "";
    return capitalizeName(u.displayName || u.name || u.email?.split("@")[0] || "User");
  };

  // Only active users can be managed (not deleted/disabled)
  const activeUsers = users.filter(u => !u.deleted && u.status !== "Disabled");

  const handleToggleMember = (email) => {
    const lowerEmail = email.toLowerCase();
    
    // Cannot remove the creator of the channel
    const creatorEmail = channel.createdByEmail?.toLowerCase();
    if (lowerEmail === creatorEmail) {
      window.alert("You cannot remove the channel creator.");
      return;
    }

    setInvitedEmails(prev =>
      prev.includes(lowerEmail)
        ? prev.filter(e => e !== lowerEmail)
        : [...prev, lowerEmail]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, "chats", channel.id);
      await updateDoc(docRef, {
        invitedMembers: invitedEmails
      });
      onClose();
    } catch (err) {
      console.error("Failed to update channel members:", err);
      window.alert("Failed to update channel members: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.75)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1100,
      backdropFilter: "blur(4px)"
    }}>
      <div 
        className="card glass-card animate-scale-up" 
        style={{
          width: "440px",
          maxWidth: "90%",
          height: "60vh",
          maxHeight: "550px",
          display: "flex",
          flexDirection: "column",
          padding: 0,
          border: "1px solid var(--border-color)",
          backgroundColor: "rgba(20, 24, 33, 0.95)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)"
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1rem 1.5rem",
          borderBottom: "1px solid var(--border-color)",
          backgroundColor: "rgba(10, 12, 18, 0.4)"
        }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <h3 style={{ margin: 0, color: "var(--primary)", fontSize: "1.1rem", fontWeight: "700" }}>
              Manage Members
            </h3>
            <span style={{ fontSize: "0.72rem", color: "var(--text-secondary)" }}>
              #{channel.name}
            </span>
          </div>
          <button 
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Users List */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0.75rem" }}>
          {activeUsers.map((u) => {
            const fullName = getFormattedName(u);
            const emailLower = u.email?.toLowerCase();
            const isInvited = invitedEmails.includes(emailLower);
            const isCreator = channel.createdByEmail?.toLowerCase() === emailLower;

            return (
              <div
                key={u.id || u.email}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.5rem 0.75rem",
                  borderRadius: "4px",
                  marginBottom: "0.25rem",
                  backgroundColor: isInvited ? "rgba(255,255,255,0.02)" : "transparent"
                }}
                className="slack-item-hover"
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", minWidth: 0 }}>
                  <div style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "4px",
                    backgroundColor: u.role === "Admin" ? "#e74c3c" : "#3498db",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "700",
                    fontSize: "0.75rem"
                  }}>
                    {fullName.substring(0, 1).toUpperCase()}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <span style={{ fontSize: "0.8rem", color: "var(--text-primary)", fontWeight: "600", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {fullName} {isCreator && <span style={{ fontSize: "0.65rem", color: "var(--primary)", marginLeft: "0.25rem" }}>(Creator)</span>}
                    </span>
                    <span style={{ fontSize: "0.68rem", color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {u.email}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  disabled={isCreator}
                  onClick={() => handleToggleMember(u.email)}
                  style={{
                    padding: "0.25rem 0.6rem",
                    fontSize: "0.7rem",
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: isInvited ? "#e74c3c" : "var(--border-color)",
                    backgroundColor: isInvited ? "rgba(231,76,60,0.08)" : "rgba(255,255,255,0.03)",
                    color: isInvited ? "#e74c3c" : "var(--text-primary)",
                    cursor: isCreator ? "not-allowed" : "pointer",
                    fontWeight: "600",
                    transition: "all 0.15s ease"
                  }}
                >
                  {isInvited ? "Remove" : "Add"}
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: "1rem 1.5rem",
          borderTop: "1px solid var(--border-color)",
          backgroundColor: "rgba(10, 12, 18, 0.4)",
          display: "flex",
          justifyContent: "flex-end",
          gap: "0.75rem"
        }}>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={onClose}
            style={{ padding: "0.45rem 1rem", fontSize: "0.8rem" }}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-primary"
            onClick={handleSave}
            style={{ padding: "0.45rem 1.25rem", fontSize: "0.8rem" }}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
